import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  AlertTriangle, 
  CheckCircle2, 
  Target, 
  BarChart3,
  ArrowRight,
  Info,
  RefreshCw,
  Database,
  Calculator,
  ChevronRight,
  Award,
  XCircle,
  LineChart as LineChartIcon,
  Calendar,
  Bell,
  BellOff,
  Settings,
  AlertCircle,
  Sparkles,
  Brain,
  Loader2,
  MessageSquareText,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Download,
  Copy,
  CalendarClock,
  Trash2,
  Send,
  Mail,
  ShieldAlert,
  X,
  FileText,
  Users,
  Building2,
  BarChartHorizontal
} from 'lucide-react';
import logger from '../lib/logger';
import { sanitizeHTML } from '../lib/sanitize';
import { cn } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import CohortSelector from '../components/CohortSelector';
import secureStorage from '../lib/secureStorage';
import { LossWaterfall } from '../components/value-conversion/LossWaterfall';
import { KpiConversionInsight } from '../components/value-conversion/KpiConversionInsight';
import { FrictionPanel } from '../components/people-engine/FrictionPanel';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend,
  ReferenceLine,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
  ComposedChart
} from 'recharts';

import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
// Unified status visual language for the whole Gap Analysis page —
// matches the per-row pill grid under the Projected vs Actual chart
// AND the summary tiles in the page header. Colour ramp:
//   • exceeding           → emerald (KPI beat the plan)
//   • on_target           → sky     (KPI within plan tolerance)
//   • below_target        → amber   (KPI underperformed)
//   • significantly_below → rose    (KPI badly underperformed)
//   • no_actual_data / no_projection → slate (data missing)
const STATUS_CONFIG = {
  exceeding: {
    label: 'Ahead of course',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    barColor: 'bg-emerald-500',
    icon: Award,
  },
  on_target: {
    label: 'On course',
    color: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-300',
    barColor: 'bg-sky-500',
    icon: CheckCircle2,
  },
  // Legacy alias — backend has historically returned `on_track`; new
  // routes return `on_target`. Keep both pointing at the same config.
  on_track: {
    label: 'On course',
    color: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-300',
    barColor: 'bg-sky-500',
    icon: CheckCircle2,
  },
  below_target: {
    label: 'Off course',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    barColor: 'bg-amber-500',
    icon: AlertTriangle,
  },
  significantly_below: {
    label: 'Far off course',
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    barColor: 'bg-rose-500',
    icon: XCircle,
  },
  no_actual_data: {
    label: 'Signal missing',
    color: 'text-slate-500',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-300',
    barColor: 'bg-slate-400',
    icon: Database,
  },
  no_projection: {
    label: 'No projection',
    color: 'text-slate-500',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-300',
    barColor: 'bg-slate-400',
    icon: Calculator,
  },
};

export default function GapAnalysisPage() {
  const navigate = useNavigate();
  const { isSuperAdmin, viewAsOrg } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [gapData, setGapData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [alertData, setAlertData] = useState(null);
  const [alertSettings, setAlertSettings] = useState({
    warning_threshold: -5.0,
    critical_threshold: -15.0,
    enable_alerts: true,
    notify_on_improvement: true,
    improvement_threshold: 10.0
  });
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [selectedKpi, setSelectedKpi] = useState('');
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(true);
  const [aiNarrative, setAiNarrative] = useState(null);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [narrativeExpanded, setNarrativeExpanded] = useState(true);
  // Comparative AI narrative (cohort A vs B)
  const [compareNarrative, setCompareNarrative] = useState(null);
  const [generatingCompareNarrative, setGeneratingCompareNarrative] = useState(false);
  const [compareNarrativeExpanded, setCompareNarrativeExpanded] = useState(true);
  // Comparative monthly digest schedules
  const [digestSchedules, setDigestSchedules] = useState([]);
  const [digestSuppressions, setDigestSuppressions] = useState([]);
  const [digestDialogOpen, setDigestDialogOpen] = useState(false);
  const [digestForm, setDigestForm] = useState({
    name: '',
    recipient_emails: '',
    day_of_month: 1,
    enabled: true,
    send_test: true,
  });
  const [savingDigestSchedule, setSavingDigestSchedule] = useState(false);
  const [sendingDigestId, setSendingDigestId] = useState(null);
  
  // Organization filter
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [allCohorts, setAllCohorts] = useState([]);

  // While previewing as a specific org, force-scope every query through
  // that org so KPI summaries / projections / actuals can't leak across
  // tenants. Mirrors the lock pattern used on Program Analytics.
  useEffect(() => {
    if (viewAsOrg?.id) setSelectedOrg(viewAsOrg.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsOrg?.id]);
  
  // Cohort filter state
  const [selectedCohort, setSelectedCohort] = useState(null);
  
  // Fetch organizations (Super Admin)
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchOrgs = async () => {
      try {
        const res = await api.get('/rbac/organizations');
        setOrganizations(res.data.organizations || []);
      } catch (e) { logger.error('Error fetching orgs:', e); }
    };
    fetchOrgs();
  }, [isSuperAdmin]);
  
  // Fetch cohorts for org filtering
  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const res = await api.get('/segments/cohorts');
        setAllCohorts(res.data.cohorts || []);
      } catch (e) { logger.error('Error fetching cohorts:', e); }
    };
    fetchCohorts();
  }, []);
  
  // Filtered cohorts by selected org
  const filteredCohorts = selectedOrg === 'all' ? allCohorts : allCohorts.filter(c => c.organization_id === selectedOrg);

  // Headers for the Missing Middle FrictionPanel (uses authedFetch
  // directly rather than the axios `api` wrapper).
  const frictionHeaders = useCallback(() => {
    const h = { Authorization: `Bearer ${secureStorage.getItem('session_token')}` };
    if (viewAsOrg?.id) h['X-View-As-Org'] = viewAsOrg.id;
    return h;
  }, [viewAsOrg]);
  
  // Reset cohort selection when org changes
  useEffect(() => {
    setSelectedCohort(null);
  }, [selectedOrg]);
  
  // Comparison mode state
  const [comparisonMode, setComparisonMode] = useState(false);
  const [cohortA, setCohortA] = useState(null);
  const [cohortB, setCohortB] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [saveComparisonDialog, setSaveComparisonDialog] = useState(false);
  const [comparisonName, setComparisonName] = useState('');
  
  // Comparison alerts state
  const [comparisonAlertRules, setComparisonAlertRules] = useState([]);
  const [activeComparisonAlerts, setActiveComparisonAlerts] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [showAlertRuleDialog, setShowAlertRuleDialog] = useState(false);
  const [showAlertHistoryDialog, setShowAlertHistoryDialog] = useState(false);
  const [newAlertRule, setNewAlertRule] = useState({
    name: '',
    gap_difference_threshold: 15.0,
    baseline_drop_threshold: -10.0,
    enable_gap_difference_alert: true,
    enable_baseline_drop_alert: true,
    in_app_notification: true,
    email_notification: false,
    frequency: 'daily'
  });
  const [recommendedThresholds, setRecommendedThresholds] = useState(null);
  
  // Benchmark overview state
  const [benchmarkOverview, setBenchmarkOverview] = useState(null);
  // Missing Middle context: journey-stage scores + dollar value-at-stake
  // (largest saved scenario) powering attribution, confidence and the
  // loss waterfall. All derived visuals are labeled "modeled".
  const [vcContext, setVcContext] = useState(null);

  // Define fetch functions BEFORE useEffects that use them
  const fetchGapAnalysis = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedCohort) params.append('cohort_id', selectedCohort.id);
      if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/import/data/gap-analysis${qs}`);
      setGapData(response.data);
    } catch (err) {
      logger.error('Failed to fetch gap analysis:', err);
      setError('Failed to load gap analysis data');
      toast.error('Failed to load gap analysis');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCohort, selectedOrg]);

  const fetchTrendData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCohort) params.append('cohort_id', selectedCohort.id);
      if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/import/data/gap-analysis/trends${qs}`);
      setTrendData(response.data);
    } catch (err) {
      logger.error('Failed to fetch trend data:', err);
    }
  }, [selectedCohort, selectedOrg]);

  const fetchComparisonData = useCallback(async () => {
    if (!cohortA || !cohortB) return;
    
    setLoadingComparison(true);
    try {
      const response = await api.get(`/import/data/gap-analysis/compare?cohort_a_id=${cohortA.id}&cohort_b_id=${cohortB.id}`);
      setComparisonData(response.data);
    } catch (err) {
      logger.error('Failed to fetch comparison data:', err);
      toast.error('Failed to load comparison data');
    } finally {
      setLoadingComparison(false);
    }
  }, [cohortA, cohortB]);

  const fetchRecommendedThresholds = useCallback(async () => {
    if (!cohortA || !cohortB) return;
    try {
      const response = await api.get(`/import/alerts/comparison-rules/recommend-thresholds?cohort_a_id=${cohortA.id}&cohort_b_id=${cohortB.id}`);
      setRecommendedThresholds(response.data);
    } catch (err) {
      logger.error('Failed to fetch recommended thresholds:', err);
    }
  }, [cohortA, cohortB]);

  // Now the useEffects can safely reference the fetch functions
  useEffect(() => {
    if (!comparisonMode) {
      fetchGapAnalysis();
      fetchTrendData();
      fetchAlertData();
      fetchAlertSettings();
      fetchCachedNarrative();
      fetchBenchmarkOverview();
      fetchVcContext();
    }
  }, [selectedCohort, comparisonMode, selectedOrg, fetchGapAnalysis, fetchTrendData]); // Re-fetch when cohort or org changes
  
  useEffect(() => {
    if (comparisonMode) {
      fetchSavedComparisons();
      fetchComparisonAlertRules();
      fetchActiveComparisonAlerts();
    }
  }, [comparisonMode]);
  
  useEffect(() => {
    if (comparisonMode && cohortA && cohortB) {
      fetchComparisonData();
      fetchRecommendedThresholds();
      fetchCachedCompareNarrative(cohortA, cohortB);
      fetchDigestSchedules(cohortA, cohortB);
    } else {
      setCompareNarrative(null);
      setDigestSchedules([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortA, cohortB, comparisonMode, fetchComparisonData, fetchRecommendedThresholds]);

  const fetchComparisonAlertRules = async () => {
    try {
      const response = await api.get('/import/alerts/comparison-rules');
      setComparisonAlertRules(response.data.rules || []);
    } catch (err) {
      logger.error('Failed to fetch alert rules:', err);
    }
  };
  
  const fetchActiveComparisonAlerts = async () => {
    try {
      const response = await api.get('/import/alerts/comparison-active');
      setActiveComparisonAlerts(response.data.alerts || []);
    } catch (err) {
      logger.error('Failed to fetch active alerts:', err);
    }
  };
  
  const fetchAlertHistory = async () => {
    try {
      const response = await api.get('/import/alerts/comparison-history');
      setAlertHistory(response.data.history || []);
    } catch (err) {
      logger.error('Failed to fetch alert history:', err);
    }
  };
  
  const fetchBenchmarkOverview = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/benchmarking/gap-analysis-overview${qs}`);
      setBenchmarkOverview(response.data);
    } catch (err) {
      logger.error('Failed to fetch benchmark overview:', err);
    }
  };

  const fetchVcContext = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
      if (selectedCohort) params.append('cohort_id', selectedCohort);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/import/data/gap-analysis/value-conversion-context${qs}`);
      setVcContext(response.data);
    } catch (err) {
      logger.error('Failed to fetch value conversion context:', err);
    }
  };
  
  const createAlertRule = async () => {
    if (!cohortA || !cohortB || !newAlertRule.name.trim()) {
      toast.error('Please select both cohorts and enter a rule name');
      return;
    }
    
    try {
      await api.post('/import/alerts/comparison-rules', {
        ...newAlertRule,
        cohort_a_id: cohortA.id,
        cohort_b_id: cohortB.id
      });
      toast.success('Alert rule created!');
      setShowAlertRuleDialog(false);
      setNewAlertRule({
        name: '',
        gap_difference_threshold: 15.0,
        baseline_drop_threshold: -10.0,
        enable_gap_difference_alert: true,
        enable_baseline_drop_alert: true,
        in_app_notification: true,
        email_notification: false,
        frequency: 'daily'
      });
      fetchComparisonAlertRules();
    } catch (err) {
      toast.error('Failed to create alert rule');
    }
  };
  
  const toggleAlertRule = async (ruleId) => {
    try {
      await api.post(`/import/alerts/comparison-rules/${ruleId}/toggle`);
      fetchComparisonAlertRules();
    } catch (err) {
      toast.error('Failed to toggle alert rule');
    }
  };
  
  const snoozeAlertRule = async (ruleId, hours = 24) => {
    try {
      await api.post(`/import/alerts/comparison-rules/${ruleId}/snooze?hours=${hours}`);
      toast.success(`Alert snoozed for ${hours} hours`);
      fetchComparisonAlertRules();
    } catch (err) {
      toast.error('Failed to snooze alert');
    }
  };
  
  const deleteAlertRule = async (ruleId) => {
    try {
      await api.delete(`/import/alerts/comparison-rules/${ruleId}`);
      toast.success('Alert rule deleted');
      fetchComparisonAlertRules();
    } catch (err) {
      toast.error('Failed to delete alert rule');
    }
  };
  
  const dismissAlert = async (alertId) => {
    try {
      await api.post(`/import/alerts/comparison-history/${alertId}/dismiss`);
      fetchActiveComparisonAlerts();
      fetchAlertHistory();
    } catch (err) {
      toast.error('Failed to dismiss alert');
    }
  };
  
  const fetchSavedComparisons = async () => {
    try {
      const response = await api.get('/import/data/gap-analysis/compare/saved');
      setSavedComparisons(response.data.comparisons || []);
    } catch (err) {
      logger.error('Failed to fetch saved comparisons:', err);
    }
  };
  
  const saveComparison = async () => {
    if (!cohortA || !cohortB || !comparisonName.trim()) {
      toast.error('Please select both cohorts and enter a name');
      return;
    }
    
    try {
      await api.post('/import/data/gap-analysis/compare/save', {
        name: comparisonName.trim(),
        cohort_a_id: cohortA.id,
        cohort_b_id: cohortB.id
      });
      toast.success('Comparison saved!');
      setSaveComparisonDialog(false);
      setComparisonName('');
      fetchSavedComparisons();
    } catch (err) {
      toast.error('Failed to save comparison');
    }
  };
  
  const loadSavedComparison = async (saved) => {
    // Need to fetch cohort details
    try {
      const response = await api.get('/segments/cohorts');
      const allCohorts = response.data.cohorts || [];
      const a = allCohorts.find(c => c.id === saved.cohort_a_id);
      const b = allCohorts.find(c => c.id === saved.cohort_b_id);
      if (a) setCohortA(a);
      if (b) setCohortB(b);
      toast.info(`Loaded: ${saved.name}`);
    } catch (err) {
      toast.error('Failed to load comparison');
    }
  };
  
  const deleteSavedComparison = async (comparisonId) => {
    try {
      await api.delete(`/import/data/gap-analysis/compare/saved/${comparisonId}`);
      toast.success('Comparison deleted');
      fetchSavedComparisons();
    } catch (err) {
      toast.error('Failed to delete comparison');
    }
  };
  
  const exportComparison = async (format) => {
    if (!cohortA || !cohortB) {
      toast.error('Please select both cohorts first');
      return;
    }
    
    try {
      const response = await api.get(
        `/import/data/gap-analysis/compare/export?cohort_a_id=${cohortA.id}&cohort_b_id=${cohortB.id}&format=${format}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cohort_comparison.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Comparison exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Failed to export comparison');
    }
  };

  const fetchAlertData = async () => {
    try {
      const response = await api.get('/import/alerts/status');
      setAlertData(response.data);
    } catch (err) {
      logger.error('Failed to fetch alert data:', err);
    }
  };

  const fetchAlertSettings = async () => {
    try {
      const response = await api.get('/import/alerts/thresholds');
      setAlertSettings(response.data);
    } catch (err) {
      logger.error('Failed to fetch alert settings:', err);
    }
  };

  const saveAlertSettings = async () => {
    setSavingSettings(true);
    try {
      await api.post('/import/alerts/thresholds', alertSettings);
      toast.success('Alert settings saved');
      setShowAlertSettings(false);
      fetchAlertData(); // Refresh alerts with new thresholds
    } catch (err) {
      logger.error('Failed to save alert settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchCachedNarrative = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/import/gap-analysis/narrative${qs}`);
      if (response.data?.narrative) {
        setAiNarrative(response.data);
      }
    } catch (err) {
      logger.error('Failed to fetch cached narrative:', err);
    }
  };

  const generateNarrative = async () => {
    setGeneratingNarrative(true);
    try {
      const params = new URLSearchParams();
      if (selectedCohort?.id) params.append('cohort_id', selectedCohort.id);
      if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
      const qs = params.toString();
      const url = qs ? `/import/gap-analysis/narrative?${qs}` : '/import/gap-analysis/narrative';
      const response = await api.post(url);
      if (response.data?.narrative) {
        setAiNarrative(response.data);
        toast.success('AI insights generated successfully');
      } else if (response.data?.error) {
        toast.error(response.data.error);
      }
    } catch (err) {
      logger.error('Failed to generate narrative:', err);
      toast.error('Failed to generate AI insights');
    } finally {
      setGeneratingNarrative(false);
    }
  };

  const formatNarrativeDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const exportNarrativePdf = async () => {
    try {
      toast.info('Generating PDF report...');
      const params = new URLSearchParams();
      if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/import/gap-analysis/narrative/export-pdf${qs}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Gap_Analysis_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report downloaded');
    } catch (err) {
      logger.error('Failed to export PDF:', err);
      if (err.response?.status === 404) {
        toast.error('Please generate AI insights first before exporting');
      } else {
        toast.error('Failed to export PDF report');
      }
    }
  };

  // ============================================
  // Comparative AI Narrative (Cohort A vs Cohort B)
  // ============================================

  const fetchCachedCompareNarrative = async (cA, cB) => {
    if (!cA?.id || !cB?.id) return;
    try {
      const params = new URLSearchParams({ cohort_a_id: cA.id, cohort_b_id: cB.id });
      const response = await api.get(`/import/gap-analysis/narrative/compare?${params}`);
      if (response.data?.narrative) {
        setCompareNarrative(response.data);
      } else {
        setCompareNarrative(null);
      }
    } catch (err) {
      logger.error('Failed to fetch cached comparative narrative:', err);
    }
  };

  const generateCompareNarrative = async () => {
    if (!cohortA?.id || !cohortB?.id) {
      toast.error('Select two cohorts to compare');
      return;
    }
    setGeneratingCompareNarrative(true);
    try {
      const params = new URLSearchParams({ cohort_a_id: cohortA.id, cohort_b_id: cohortB.id });
      if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
      const response = await api.post(`/import/gap-analysis/narrative/compare?${params}`);
      if (response.data?.narrative) {
        setCompareNarrative(response.data);
        toast.success('Comparative AI insights generated');
      } else if (response.data?.error) {
        toast.error(response.data.error);
      }
    } catch (err) {
      logger.error('Failed to generate comparative narrative:', err);
      toast.error('Failed to generate comparative insights');
    } finally {
      setGeneratingCompareNarrative(false);
    }
  };

  const copyCompareNarrative = async () => {
    if (!compareNarrative?.narrative) return;
    try {
      const header = `Cohort Comparative Analysis: ${compareNarrative.cohort_a?.name || 'A'} vs ${compareNarrative.cohort_b?.name || 'B'}\n\n`;
      await navigator.clipboard.writeText(header + compareNarrative.narrative);
      toast.success('Copied to clipboard');
    } catch (err) {
      logger.error('Clipboard copy failed:', err);
      toast.error('Could not copy to clipboard');
    }
  };

  const exportCompareNarrativePdf = async () => {
    if (!cohortA?.id || !cohortB?.id) return;
    try {
      toast.info('Generating PDF report...');
      const params = new URLSearchParams({ cohort_a_id: cohortA.id, cohort_b_id: cohortB.id });
      const response = await api.get(
        `/import/gap-analysis/narrative/compare/export-pdf?${params}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateSlug = new Date().toISOString().split('T')[0];
      link.setAttribute(
        'download',
        `Cohort_Comparison_${(cohortA.name || 'A').replace(/\s+/g, '_')}_vs_${(cohortB.name || 'B').replace(/\s+/g, '_')}_${dateSlug}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (err) {
      logger.error('Failed to export comparative PDF:', err);
      if (err.response?.status === 404) {
        toast.error('Please generate comparative insights first before exporting');
      } else {
        toast.error('Failed to export PDF report');
      }
    }
  };

  // ============================================
  // Monthly Comparative Digest Schedules
  // ============================================

  const fetchDigestSchedules = async (cA, cB) => {
    if (!cA?.id || !cB?.id) {
      setDigestSchedules([]);
      setDigestSuppressions([]);
      return;
    }
    try {
      const params = new URLSearchParams({ cohort_a_id: cA.id, cohort_b_id: cB.id });
      const [schedRes, supRes] = await Promise.all([
        api.get(`/import/gap-analysis/digest-schedules?${params}`),
        api.get('/import/gap-analysis/digest-schedules/suppressions'),
      ]);
      setDigestSchedules(Array.isArray(schedRes.data) ? schedRes.data : []);
      setDigestSuppressions(Array.isArray(supRes.data) ? supRes.data : []);
    } catch (err) {
      logger.error('Failed to fetch digest schedules:', err);
    }
  };

  const dismissSuppression = async (sup) => {
    try {
      await api.post(`/import/gap-analysis/digest-schedules/suppressions/${sup.id}/dismiss`);
      setDigestSuppressions((prev) => prev.filter((s) => s.id !== sup.id));
    } catch (err) {
      logger.error('Failed to dismiss suppression:', err);
      toast.error('Could not dismiss alert');
    }
  };

  const openDigestDialog = () => {
    if (!cohortA?.id || !cohortB?.id) return;
    setDigestForm({
      name: `${cohortA.name} vs ${cohortB.name} — Monthly`,
      recipient_emails: '',
      day_of_month: 1,
      enabled: true,
      send_test: true,
    });
    setDigestDialogOpen(true);
  };

  const saveDigestSchedule = async () => {
    if (!cohortA?.id || !cohortB?.id) return;
    const emails = digestForm.recipient_emails
      .split(/[,;\n]/)
      .map((e) => e.trim())
      .filter((e) => e && e.includes('@'));
    if (!digestForm.name.trim()) {
      toast.error('Schedule name is required');
      return;
    }
    if (emails.length === 0) {
      toast.error('Add at least one valid recipient email');
      return;
    }
    setSavingDigestSchedule(true);
    try {
      const body = {
        name: digestForm.name.trim(),
        cohort_a_id: cohortA.id,
        cohort_b_id: cohortB.id,
        recipient_emails: emails,
        day_of_month: Number(digestForm.day_of_month) || 1,
        enabled: !!digestForm.enabled,
        send_test: !!digestForm.send_test,
      };
      if (selectedOrg && selectedOrg !== 'all') body.organization_id = selectedOrg;
      const response = await api.post('/import/gap-analysis/digest-schedules', body);
      const ts = response.data?.test_send;
      if (ts) {
        if (ts.sent) {
          toast.success(`Schedule saved · test email sent to ${ts.recipients} recipient(s)`);
        } else {
          toast.warning(`Schedule saved, but test send failed: ${ts.reason || 'unknown'}`);
        }
      } else {
        toast.success('Monthly digest scheduled');
      }
      setDigestDialogOpen(false);
      fetchDigestSchedules(cohortA, cohortB);
    } catch (err) {
      logger.error('Failed to save digest schedule:', err);
      toast.error('Failed to save schedule');
    } finally {
      setSavingDigestSchedule(false);
    }
  };

  const toggleDigestSchedule = async (schedule) => {
    try {
      await api.patch(`/import/gap-analysis/digest-schedules/${schedule.id}`, {
        enabled: !schedule.enabled,
      });
      toast.success(schedule.enabled ? 'Schedule paused' : 'Schedule resumed');
      fetchDigestSchedules(cohortA, cohortB);
    } catch (err) {
      logger.error('Failed to toggle schedule:', err);
      toast.error('Failed to update schedule');
    }
  };

  const deleteDigestSchedule = async (schedule) => {
    if (!window.confirm(`Delete "${schedule.name}"?`)) return;
    try {
      await api.delete(`/import/gap-analysis/digest-schedules/${schedule.id}`);
      toast.success('Schedule deleted');
      fetchDigestSchedules(cohortA, cohortB);
    } catch (err) {
      logger.error('Failed to delete schedule:', err);
      toast.error('Failed to delete schedule');
    }
  };

  const sendDigestNow = async (schedule) => {
    setSendingDigestId(schedule.id);
    try {
      const response = await api.post(`/import/gap-analysis/digest-schedules/${schedule.id}/send-now`);
      if (response.data?.sent) {
        toast.success(`Digest sent to ${response.data.recipients} recipient(s)`);
        fetchDigestSchedules(cohortA, cohortB);
      } else {
        toast.error(`Could not send: ${response.data?.reason || 'unknown error'}`);
      }
    } catch (err) {
      logger.error('Failed to send digest now:', err);
      toast.error('Failed to send digest');
    } finally {
      setSendingDigestId(null);
    }
  };

  const formatScheduleDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const lastStatusBadge = (lastStatus) => {
    if (!lastStatus || !lastStatus.status) {
      return { label: 'Pending', className: 'bg-slate-100 text-slate-600' };
    }
    const STATUS_BADGES = {
      delivered: { label: 'Delivered', className: 'bg-emerald-100 text-emerald-700' },
      sent: { label: 'Sent', className: 'bg-blue-100 text-blue-700' },
      bounced: { label: 'Bounced', className: 'bg-rose-100 text-rose-700' },
      complained: { label: 'Spam complaint', className: 'bg-rose-100 text-rose-700' },
      delayed: { label: 'Delayed', className: 'bg-amber-100 text-amber-700' },
      failed: { label: 'Failed', className: 'bg-rose-100 text-rose-700' },
      opened: { label: 'Opened', className: 'bg-emerald-100 text-emerald-700' },
      clicked: { label: 'Clicked', className: 'bg-emerald-100 text-emerald-700' },
    };
    return (
      STATUS_BADGES[lastStatus.status] || {
        label: lastStatus.status,
        className: 'bg-slate-100 text-slate-700',
      }
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Prepare chart data
  const getChartData = () => {
    if (!gapData?.gap_analysis) return [];

    return Object.values(gapData.gap_analysis)
      .filter(g => g.has_projection || g.has_actual)
      .map(g => ({
        // Use the full KPI name (already concise — "Safety Incident Rate"
        // etc.). The previous `split(' ')[0]` truncated names to single
        // words ("Customer", "Output", "Time-to-Market" → "Time-to-Market").
        name: g.kpi_name || g.kpi_id,
        kpiId: g.kpi_id,
        fullName: g.kpi_name,
        projected: g.projected_improvement,
        actual: g.actual_improvement,
        gap: g.gap,
        gapPercent: g.gap_percent,
        status: g.status,
      }))
      .sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap));
  };

  const chartData = getChartData();

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      const statusConfig = STATUS_CONFIG[data?.status] || STATUS_CONFIG.no_actual_data;
      
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-slate-200 max-w-xs">
          <p className="font-semibold text-slate-800 mb-2">{data?.fullName}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-slate-500">Projected:</span>
              <span className="font-medium text-purple-600">{data?.projected?.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-slate-500">Actual:</span>
              <span className="font-medium text-emerald-600">{data?.actual?.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between gap-4 pt-1 border-t">
              <span className="text-slate-500">Gap:</span>
              <span className={`font-medium ${data?.gap >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {data?.gap >= 0 ? '+' : ''}{data?.gap?.toFixed(1)}%
              </span>
            </div>
          </div>
          <Badge className={`mt-2 ${statusConfig.bgColor} ${statusConfig.color} border ${statusConfig.borderColor}`}>
            {statusConfig.label}
          </Badge>
        </div>
      );
    }
    return null;
  };

  // Prepare trend chart data
  const getTrendChartData = () => {
    if (!trendData?.time_series?.length) return [];
    
    return trendData.time_series.map(point => ({
      date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullDate: point.date,
      averageGap: point.average_gap,
      kpiCount: point.kpi_count,
      kpiData: point.kpi_data
    }));
  };

  // Get individual KPI trend data
  const getKpiTrendData = (kpiId) => {
    if (!trendData?.kpi_trends?.[kpiId]) return [];
    
    const kpiTrend = trendData.kpi_trends[kpiId];
    return kpiTrend.data_points.map(point => ({
      date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullDate: point.date,
      actual: point.improvement,
      projected: point.projected,
      gap: point.gap
    }));
  };

  // Get available KPIs for the trend selector
  const getAvailableKpis = () => {
    if (!trendData?.kpi_trends) return [];
    return Object.entries(trendData.kpi_trends).map(([id, data]) => ({
      id,
      name: data.kpi_name
    }));
  };

  // Custom tooltip for trend charts
  const TrendTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200 text-sm">
          <p className="font-medium text-slate-800 mb-2">{data?.fullDate || label}</p>
          {payload.map((entry, index) => (
            <div key={`gap-${entry.dataKey || entry.name || index}`} className="flex justify-between gap-4">
              <span style={{ color: entry.color }}>{entry.name}:</span>
              <span className="font-medium">{entry.value?.toFixed(2)}%</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const trendChartData = getTrendChartData();
  const availableKpis = getAvailableKpis();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading value conversion analysis...</p>
        </div>
      </div>
    );
  }

  // The /import/data/gap-analysis endpoint returns canonical field
  // names (average_gap, exceeding_projection, below_target) while the
  // UI/stat-cards below were written against avg_gap / exceeding_count
  // / below_count. Alias them here in one place so every downstream
  // reference resolves (the bug was masked while the endpoint returned
  // all-zeros for unscoped orgs).
  const rawSummary = gapData?.summary || {};
  const summary = {
    ...rawSummary,
    avg_gap: rawSummary.avg_gap ?? rawSummary.average_gap,
    exceeding_count: rawSummary.exceeding_count ?? rawSummary.exceeding_projection,
    below_count: rawSummary.below_count ?? rawSummary.below_target,
  };
  const hasData = summary.kpis_with_both > 0;
  const hasProjections = summary.kpis_with_projections > 0;
  const hasActuals = summary.kpis_with_actuals > 0;

  // Friction panel org scope — empty string lets the backend resolve
  // the org from RBAC (org admins / previewed org).
  const frictionOrgId = selectedOrg && selectedOrg !== 'all' ? selectedOrg : (viewAsOrg?.id || '');

  const __pageTheme = getLightSectionTheme('Navy');
  const __heroActions = (
    <>
      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
        <Label htmlFor="comparison-mode" className="text-xs text-slate-900 cursor-pointer">
          Compare Cohorts
        </Label>
        <Switch
          id="comparison-mode"
          checked={comparisonMode}
          onCheckedChange={(checked) => {
            setComparisonMode(checked);
            if (!checked) {
              setCohortA(null);
              setCohortB(null);
              setComparisonData(null);
            }
          }}
          data-testid="comparison-mode-toggle"
        />
      </div>
      <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50 h-8">
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            Alert Settings
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-500" />
              Alert Threshold Settings
            </DialogTitle>
            <DialogDescription>
              Configure when you want to be alerted about gaps between projections and actuals.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="enable-alerts" className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Enable Alerts
              </Label>
              <Switch
                id="enable-alerts"
                checked={alertSettings.enable_alerts}
                onCheckedChange={(checked) => setAlertSettings(s => ({ ...s, enable_alerts: checked }))}
              />
            </div>
            <div className="space-y-4 pt-2 border-t">
              <h4 className="font-medium text-sm text-slate-700">Underperformance Alerts</h4>
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Warning Threshold (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={alertSettings.warning_threshold}
                    onChange={(e) => setAlertSettings(s => ({ ...s, warning_threshold: parseFloat(e.target.value) || 0 }))}
                    className="w-24"
                    step="1"
                  />
                  <span className="text-sm text-slate-500">Alert when gap falls below this</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Critical Threshold (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={alertSettings.critical_threshold}
                    onChange={(e) => setAlertSettings(s => ({ ...s, critical_threshold: parseFloat(e.target.value) || 0 }))}
                    className="w-24"
                    step="1"
                  />
                  <span className="text-sm text-slate-500">Critical alert threshold</span>
                </div>
              </div>
            </div>
            <div className="space-y-4 pt-2 border-t">
              <div className="flex items-center justify-between">
                <Label htmlFor="notify-improvement" className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-emerald-500" />
                  Notify on Exceeding
                </Label>
                <Switch
                  id="notify-improvement"
                  checked={alertSettings.notify_on_improvement}
                  onCheckedChange={(checked) => setAlertSettings(s => ({ ...s, notify_on_improvement: checked }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Exceeding Threshold (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={alertSettings.improvement_threshold}
                    onChange={(e) => setAlertSettings(s => ({ ...s, improvement_threshold: parseFloat(e.target.value) || 0 }))}
                    className="w-24"
                    step="1"
                  />
                  <span className="text-sm text-slate-500">Celebrate when exceeding by this much</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertSettings(false)}>
              Cancel
            </Button>
            <Button onClick={saveAlertSettings} disabled={savingSettings}>
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Button variant="outline" size="sm" onClick={() => { fetchGapAnalysis(); fetchAlertData(); }} className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50 h-8">
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
        Refresh
      </Button>
    </>
  );
  const onCourseCount = (summary.exceeding_count || 0) + (summary.on_track || 0);
  const offCourseCount = summary.below_count || 0;
  const navSuccessRate = summary.kpis_with_both > 0
    ? Math.round((onCourseCount / summary.kpis_with_both) * 100)
    : null;
  const __heroStats = [
    {
      label: 'Navigation Success Rate',
      value: navSuccessRate != null ? `${navSuccessRate}%` : '—',
      testId: 'gap-summary-success-rate',
      whyPanel: {
        label: 'Navigation Success Rate',
        value: navSuccessRate != null ? `${navSuccessRate}%` : '—',
        description: 'Share of tracked KPIs that are on-course (ahead of or on plan). The headline answer to "are we navigating successfully?" rather than "how many KPIs missed?".',
        formula: 'on-course KPIs ÷ KPIs tracked × 100',
        breakdown: [
          { label: 'How to consider it', value: '', hint: '80%+ means navigation is working. 50–80% means value is leaking on specific journeys. Below 50% indicates a systemic Missing Middle problem — capability is not converting into outcomes.' },
          { label: 'What to do', value: '', hint: navSuccessRate == null
              ? 'No comparable KPIs yet — import KPI actuals and save a scenario so expected vs realized value can be compared.'
              : navSuccessRate >= 80
                ? 'Strong navigation. Use the Executive Summary to anchor your CFO narrative and re-baseline conservative projections.'
                : 'Open the off-course KPIs below and check their Primary Contributors — the leak is usually in confidence or coordination, not capability.' },
        ],
        links: [
          { label: 'Open Navigation Readiness Diagnostic', path: '/people-engine/diagnostic' },
          { label: 'Import KPI data', path: '/data-management' },
        ],
      },
    },
    {
      label: 'Value Conversion Gap',
      value: (summary.avg_gap != null ? `${summary.avg_gap > 0 ? '+' : ''}${summary.avg_gap.toFixed(1)}%` : '0%'),
      testId: 'gap-summary-conversion-gap',
      whyPanel: {
        label: 'Value Conversion Gap',
        value: (summary.avg_gap != null ? `${summary.avg_gap > 0 ? '+' : ''}${summary.avg_gap.toFixed(1)}%` : '0%'),
        description: 'Mean difference between the value creation we expected and the value we realized, across every tracked KPI. Negative = value leaked between capability and outcomes.',
        formula: 'mean((realized − expected) ÷ expected × 100)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: '0% to +5% is healthy realism. Below -10% means expected value is not converting — investigate the journey stages before funding more programs.' },
          { label: 'What to do', value: '', hint: summary.avg_gap == null
              ? 'No data yet — import KPI actuals to begin value conversion analysis.'
              : summary.avg_gap < -10
                ? 'Open the Loss Waterfall and Friction Analysis to see which journey stage is leaking the most value.'
                : summary.avg_gap > 10
                  ? 'You are systematically under-projecting — re-baseline assumptions in Business Case Builder.'
                  : 'Healthy band. Keep monitoring and use Cohort Comparison to spot which segments convert best.' },
        ],
        links: [
          { label: 'Open Cohort Comparison', path: '/comparison-analytics' },
          { label: 'Re-tune projections', path: '/business-case-builder' },
        ],
      },
    },
    {
      label: 'On-Course',
      value: onCourseCount,
      testId: 'gap-summary-on-course',
      whyPanel: {
        label: 'On-Course KPIs',
        value: onCourseCount,
        valueSuffix: 'KPIs',
        description: 'KPIs converting expected value into realized value — ahead of or on plan. These journeys are navigating successfully.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'On-course KPIs are proof the full journey worked: capability converted into confidence, coordination, and outcomes. They are your strongest CFO anecdotes.' },
          { label: 'What to do', value: '', hint: onCourseCount === 0
              ? 'Nothing on-course yet — normal early in a program; re-check after another period of actuals.'
              : 'Pin the strongest performer to your Quarterly Deck, then re-baseline so future scenarios capture the upside.' },
        ],
        links: [
          { label: 'Pin a scenario', path: '/scenarios' },
          { label: 'Open Quarterly Deck', path: '/' },
        ],
      },
    },
    {
      label: 'Off-Course',
      value: offCourseCount,
      testId: 'gap-summary-off-course',
      whyPanel: {
        label: 'Off-Course KPIs',
        value: offCourseCount,
        valueSuffix: 'KPIs',
        description: 'KPIs where expected value failed to convert. The question is not "why did the KPI miss?" — it is "where along the journey did conversion break down?"',
        breakdown: [
          { label: 'How to consider it', value: '', hint: '1–2 off-course KPIs usually means a transfer/confidence leak on specific journeys. 5+ in a period suggests systemic over-projection or a coordination breakdown.' },
          { label: 'What to do', value: '', hint: offCourseCount === 0
              ? 'Nothing off-course — your value conversion is tracking. Anchor your CFO update on this.'
              : 'Check the Primary Contributors under each off-course KPI below, then open the Anomaly Dashboard to see if alerts already fired.' },
        ],
        links: [
          { label: 'Open Anomaly Dashboard', path: '/anomalies' },
          { label: 'Tune Alert Thresholds', path: '/gap-analysis' },
        ],
      },
    },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Value Conversion Analysis"
        description="We expected value — we got something else. Why? Detect where expected value failed to convert into realized outcomes, and trace the leak through the journey from capability to confidence, coordination, navigation, and value."
        theme={__pageTheme}
        icon={BarChartHorizontal}
        actions={__heroActions}
        stats={__heroStats}
        testId="gap-analysis-hero"
      />
    )}>
    <div className="space-y-6" data-testid="gap-analysis-page">
      {/* Filters Toolbar (lifted out of hero for clarity) */}
      {!comparisonMode && (
        <Card className="border-slate-200">
          <CardContent className="py-3 flex flex-wrap items-center gap-3">
            {isSuperAdmin && organizations.length > 0 && !viewAsOrg?.id && (
              <select
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                className="text-sm rounded-lg px-3 py-1.5 bg-slate-50 text-slate-800 border border-slate-200 focus:ring-2 focus:ring-teal-500 min-w-[180px]"
                data-testid="gap-org-filter"
              >
                <option value="all">All Organizations</option>
                {organizations.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            )}
            {viewAsOrg?.id && (
              <span
                className="text-sm rounded-lg px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1.5 min-w-[180px] justify-center"
                data-testid="gap-org-filter-locked"
                title="Org filter is locked while previewing — exit preview to widen the view."
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                Scoped to {viewAsOrg.name || 'Preview Org'}
              </span>
            )}
            <CohortSelector
              selectedCohort={selectedCohort}
              onCohortChange={(cohort) => {
                setSelectedCohort(cohort);
                if (cohort) {
                  toast.info(`Filtering by cohort: ${cohort.name}`);
                } else {
                  toast.info('Showing all data');
                }
              }}
              placeholder="Filter by Cohort"
              className="min-w-[200px]"
              showCreateOption={false}
              cohorts={filteredCohorts.length > 0 ? filteredCohorts : undefined}
            />
          </CardContent>
        </Card>
      )}
        
        {/* Cohort Info Banner */}
        {selectedCohort && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-900">Filtering by Cohort: {selectedCohort.name}</h3>
                  <p className="text-sm text-purple-600">
                    {gapData?.cohort?.demographics?.population_size 
                      ? `${gapData.cohort.demographics.population_size.toLocaleString()} employees`
                      : 'Showing data specific to this target population'}
                    {gapData?.cohort?.demographics?.total_workforce && gapData?.cohort?.demographics?.population_size && (
                      <span className="ml-2">
                        ({((gapData.cohort.demographics.population_size / gapData.cohort.demographics.total_workforce) * 100).toFixed(1)}% of workforce)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCohort(null)}
                className="text-purple-600 hover:text-purple-800 hover:bg-purple-100"
              >
                Clear Filter
              </Button>
            </div>
          </div>
        )}

        {/* Comparison Mode UI */}
        {comparisonMode && (
          <div className="space-y-6">
            {/* Cohort Selection Panel */}
            <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-indigo-600" />
                    Cohort Comparison Mode
                  </div>
                  <div className="flex items-center gap-2">
                    {savedComparisons.length > 0 && (
                      <Select onValueChange={(id) => {
                        const saved = savedComparisons.find(s => s.id === id);
                        if (saved) loadSavedComparison(saved);
                      }}>
                        <SelectTrigger className="w-[200px] bg-white">
                          <SelectValue placeholder="Load saved comparison" />
                        </SelectTrigger>
                        <SelectContent>
                          {savedComparisons.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSaveComparisonDialog(true)}
                      disabled={!cohortA || !cohortB}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Save View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAlertRuleDialog(true)}
                      disabled={!cohortA || !cohortB}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="manage-alerts-btn"
                    >
                      <Bell className="h-4 w-4 mr-1" />
                      Create Alert
                      {comparisonAlertRules.length > 0 && (
                        <Badge className="ml-1 bg-amber-100 text-amber-700 text-xs">
                          {comparisonAlertRules.length}
                        </Badge>
                      )}
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  {/* Cohort A Selector */}
                  <div className="space-y-2">
                    <Label className="text-indigo-700 font-medium">Cohort A</Label>
                    <CohortSelector
                      selectedCohort={cohortA}
                      onCohortChange={setCohortA}
                      placeholder="Select first cohort"
                      className="bg-white"
                      showCreateOption={false}
                    />
                    {cohortA?.demographics?.population_size && (
                      <p className="text-sm text-indigo-600">
                        {cohortA.demographics.population_size.toLocaleString()} employees
                      </p>
                    )}
                  </div>
                  
                  {/* Cohort B Selector */}
                  <div className="space-y-2">
                    <Label className="text-purple-700 font-medium">Cohort B</Label>
                    <CohortSelector
                      selectedCohort={cohortB}
                      onCohortChange={setCohortB}
                      placeholder="Select second cohort"
                      className="bg-white"
                      showCreateOption={false}
                    />
                    {cohortB?.demographics?.population_size && (
                      <p className="text-sm text-purple-600">
                        {cohortB.demographics.population_size.toLocaleString()} employees
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Comparison Results */}
            {loadingComparison ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
                  <p className="text-slate-600">Loading comparison data...</p>
                </CardContent>
              </Card>
            ) : comparisonData ? (
              <>
                {/* Summary Cards Side-by-Side */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Cohort A Summary */}
                  <Card className="border-2 border-indigo-200">
                    <CardHeader className="bg-gradient-to-r from-indigo-100 to-indigo-50">
                      <CardTitle className="flex items-center justify-between text-indigo-800">
                        <span>{comparisonData.summary.cohort_a_name}</span>
                        <Badge className={comparisonData.summary.overall_better === 'cohort_a' ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' : 'bg-slate-100 text-slate-600'}>
                          {comparisonData.summary.overall_better === 'cohort_a' ? 'Better Overall' : 'Behind'}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-lg p-3 bg-indigo-50 border border-indigo-200 text-center" data-testid="cohort-a-avg-gap">
                          <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Avg Gap</p>
                          <p className={cn(
                            'text-2xl font-bold mt-1 tabular-nums',
                            (comparisonData.cohort_a.summary.average_gap ?? 0) >= 0 ? 'text-indigo-800' : 'text-rose-700',
                          )}>
                            {(comparisonData.cohort_a.summary.average_gap ?? 0) >= 0 ? '+' : ''}{comparisonData.cohort_a.summary.average_gap}%
                          </p>
                        </div>
                        <div className="rounded-lg p-3 bg-emerald-50 border border-emerald-200 text-center" data-testid="cohort-a-exceeding">
                          <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold inline-flex items-center gap-1">
                            <Award className="h-3 w-3" /> Exceeding
                          </p>
                          <p className="text-2xl font-bold mt-1 tabular-nums text-emerald-800">{comparisonData.cohort_a.summary.exceeding_count}</p>
                        </div>
                        <div className="rounded-lg p-3 bg-amber-50 border border-amber-200 text-center" data-testid="cohort-a-below">
                          <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold inline-flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> Below
                          </p>
                          <p className="text-2xl font-bold mt-1 tabular-nums text-amber-800">{comparisonData.cohort_a.summary.below_count}</p>
                        </div>
                      </div>
                      {comparisonData.cohort_a.cohort?.demographics?.population_size && (
                        <p className="text-center text-sm text-slate-500 mt-4">
                          Population: {comparisonData.cohort_a.cohort.demographics.population_size.toLocaleString()}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Cohort B Summary */}
                  <Card className="border-2 border-purple-200">
                    <CardHeader className="bg-gradient-to-r from-purple-100 to-purple-50">
                      <CardTitle className="flex items-center justify-between text-purple-800">
                        <span>{comparisonData.summary.cohort_b_name}</span>
                        <Badge className={comparisonData.summary.overall_better === 'cohort_b' ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' : 'bg-slate-100 text-slate-600'}>
                          {comparisonData.summary.overall_better === 'cohort_b' ? 'Better Overall' : 'Behind'}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-lg p-3 bg-purple-50 border border-purple-200 text-center" data-testid="cohort-b-avg-gap">
                          <p className="text-[10px] uppercase tracking-wider text-purple-700 font-semibold">Avg Gap</p>
                          <p className={cn(
                            'text-2xl font-bold mt-1 tabular-nums',
                            (comparisonData.cohort_b.summary.average_gap ?? 0) >= 0 ? 'text-purple-800' : 'text-rose-700',
                          )}>
                            {(comparisonData.cohort_b.summary.average_gap ?? 0) >= 0 ? '+' : ''}{comparisonData.cohort_b.summary.average_gap}%
                          </p>
                        </div>
                        <div className="rounded-lg p-3 bg-emerald-50 border border-emerald-200 text-center" data-testid="cohort-b-exceeding">
                          <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold inline-flex items-center gap-1">
                            <Award className="h-3 w-3" /> Exceeding
                          </p>
                          <p className="text-2xl font-bold mt-1 tabular-nums text-emerald-800">{comparisonData.cohort_b.summary.exceeding_count}</p>
                        </div>
                        <div className="rounded-lg p-3 bg-amber-50 border border-amber-200 text-center" data-testid="cohort-b-below">
                          <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold inline-flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> Below
                          </p>
                          <p className="text-2xl font-bold mt-1 tabular-nums text-amber-800">{comparisonData.cohort_b.summary.below_count}</p>
                        </div>
                      </div>
                      {comparisonData.cohort_b.cohort?.demographics?.population_size && (
                        <p className="text-center text-sm text-slate-500 mt-4">
                          Population: {comparisonData.cohort_b.cohort.demographics.population_size.toLocaleString()}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
                
                {/* KPI-by-KPI Comparison Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        KPI Performance Comparison
                      </span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => exportComparison('pdf')}>
                          <FileText className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => exportComparison('excel')}>
                          <Download className="h-4 w-4 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={comparisonData.comparison}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={['dataMin - 5', 'dataMax + 5']} tickFormatter={(v) => `${v}%`} />
                          <YAxis type="category" dataKey="kpi_name" width={90} tick={{ fontSize: 11 }} />
                          <Tooltip 
                            formatter={(value, name) => [`${value}%`, name]}
                            contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0' }}
                          />
                          <Legend />
                          <ReferenceLine x={0} stroke="#666" strokeDasharray="3 3" />
                          <Bar dataKey="cohort_a.gap" name={comparisonData.summary.cohort_a_name} fill="#6366f1" radius={[0, 4, 4, 0]} />
                          <Bar dataKey="cohort_b.gap" name={comparisonData.summary.cohort_b_name} fill="#a855f7" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Detailed Comparison Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Detailed KPI Comparison
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="px-4 py-3 text-left font-semibold">KPI</th>
                            <th className="px-4 py-3 text-center font-semibold text-indigo-700">{comparisonData.summary.cohort_a_name}</th>
                            <th className="px-4 py-3 text-center font-semibold text-purple-700">{comparisonData.summary.cohort_b_name}</th>
                            <th className="px-4 py-3 text-center font-semibold">Difference</th>
                            <th className="px-4 py-3 text-center font-semibold">Better</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonData.comparison.map((comp, idx) => (
                            <tr key={comp.kpi_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="px-4 py-3 font-medium">{comp.kpi_name}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`font-semibold ${comp.cohort_a.gap >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {comp.cohort_a.gap >= 0 ? '+' : ''}{comp.cohort_a.gap}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`font-semibold ${comp.cohort_b.gap >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {comp.cohort_b.gap >= 0 ? '+' : ''}{comp.cohort_b.gap}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`font-semibold ${comp.gap_difference > 0 ? 'text-indigo-600' : comp.gap_difference < 0 ? 'text-purple-600' : 'text-slate-500'}`}>
                                  {comp.gap_difference > 0 ? '+' : ''}{comp.gap_difference}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {comp.better_performer === 'cohort_a' ? (
                                  <Badge className="bg-indigo-100 text-indigo-700">
                                    <ArrowRight className="h-3 w-3 mr-1 rotate-[-45deg]" />
                                    A
                                  </Badge>
                                ) : comp.better_performer === 'cohort_b' ? (
                                  <Badge className="bg-purple-100 text-purple-700">
                                    <ArrowRight className="h-3 w-3 mr-1 rotate-[45deg]" />
                                    B
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">Equal</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : cohortA && cohortB ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-slate-600">No comparison data available for selected cohorts</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Select Two Cohorts to Compare</h3>
                  <p className="text-slate-500">Choose cohorts above to see side-by-side performance comparison</p>
                </CardContent>
              </Card>
            )}

            {/* Comparative AI Insights — appears when both cohorts are selected */}
            {cohortA && cohortB && (
              <Card
                className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/60 via-white to-emerald-50/40"
                data-testid="comparative-ai-insights-card"
              >
                <Collapsible open={compareNarrativeExpanded} onOpenChange={setCompareNarrativeExpanded}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <Sparkles className="h-5 w-5 text-indigo-600" />
                        <CardTitle className="text-base">Comparative AI Insights</CardTitle>
                        {compareNarrative?.generated_at && (
                          <Badge variant="secondary" className="text-xs whitespace-nowrap">
                            Generated {formatNarrativeDate(compareNarrative.generated_at)}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {compareNarrative?.narrative && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={copyCompareNarrative}
                              data-testid="comparative-copy-btn"
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={exportCompareNarrativePdf}
                              data-testid="comparative-export-pdf-btn"
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Export PDF
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={openDigestDialog}
                          data-testid="comparative-schedule-btn"
                        >
                          <CalendarClock className="h-4 w-4 mr-2" />
                          Schedule Digest
                        </Button>
                        <Button
                          size="sm"
                          onClick={generateCompareNarrative}
                          disabled={generatingCompareNarrative}
                          className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid="comparative-generate-btn"
                        >
                          {generatingCompareNarrative ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating…
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              {compareNarrative?.narrative ? 'Regenerate' : 'Generate Comparative Insights'}
                            </>
                          )}
                        </Button>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid="comparative-toggle-btn">
                            <ChevronRight
                              className={`h-5 w-5 text-slate-400 transition-transform ${compareNarrativeExpanded ? 'rotate-90' : ''}`}
                            />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>
                    <CardDescription>
                      Side-by-side board-pack-ready analysis: {cohortA.name} vs {cohortB.name}
                    </CardDescription>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="pt-0 space-y-5">
                      {/* Side-by-side summary chips */}
                      {(compareNarrative?.summary_a || compareNarrative?.summary_b) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="comparative-summary-grid">
                          {[
                            { key: 'a', label: cohortA.name, summary: compareNarrative?.summary_a, borderClass: 'border-emerald-200' },
                            { key: 'b', label: cohortB.name, summary: compareNarrative?.summary_b, borderClass: 'border-rose-200' }
                          ].map(({ key, label, summary, borderClass }) => (
                            <div
                              key={key}
                              className={`rounded-xl border p-4 bg-white border border-slate-200 backdrop-blur-sm ${borderClass}`}
                              data-testid={`comparative-summary-${key}`}
                            >
                              <div className="text-sm font-semibold text-slate-700 mb-3 truncate">{label}</div>
                              {summary ? (
                                <div className="grid grid-cols-4 gap-2 text-center">
                                  <div>
                                    <div className="text-xl font-bold text-emerald-600">{summary.exceeding}</div>
                                    <div className="text-[11px] text-slate-500">Exceeding</div>
                                  </div>
                                  <div>
                                    <div className="text-xl font-bold text-blue-600">{summary.on_track}</div>
                                    <div className="text-[11px] text-slate-500">On Track</div>
                                  </div>
                                  <div>
                                    <div className="text-xl font-bold text-rose-600">{summary.below_target}</div>
                                    <div className="text-[11px] text-slate-500">Below</div>
                                  </div>
                                  <div>
                                    <div className={`text-xl font-bold ${summary.avg_gap >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                      {summary.avg_gap >= 0 ? '+' : ''}{summary.avg_gap.toFixed(1)}%
                                    </div>
                                    <div className="text-[11px] text-slate-500">Avg Gap</div>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-slate-500 italic">No data — generate insights first.</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Narrative */}
                      {compareNarrative?.narrative ? (
                        <div className="prose prose-sm max-w-none text-slate-700" data-testid="comparative-narrative-body">
                          {compareNarrative.narrative.split('\n').map((paragraph, idx) => {
                            const trimmed = paragraph.trim();
                            if (!trimmed) return null;
                            if (trimmed.startsWith('### ')) {
                              return (
                                <h4 key={`narr-h4-${idx}`} className="font-semibold text-slate-800 mt-4 mb-1">
                                  {trimmed.replace(/^###\s+/, '').replace(/\*\*/g, '')}
                                </h4>
                              );
                            }
                            if (trimmed.startsWith('## ')) {
                              return (
                                <h3 key={`narr-h3-${idx}`} className="font-bold text-slate-900 mt-5 mb-2">
                                  {trimmed.replace(/^##\s+/, '').replace(/\*\*/g, '')}
                                </h3>
                              );
                            }
                            if (/^[-•*]\s/.test(trimmed)) {
                              const inner = trimmed.replace(/^[-•*]\s/, '').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
                              return (
                                // eslint-disable-next-line react/no-danger -- sanitised via DOMPurify (lib/sanitize)
                                <p key={`narr-li-${idx}`} className="ml-4 text-sm" dangerouslySetInnerHTML={{ __html: sanitizeHTML(`• ${inner}`) }} />
                              );
                            }
                            const formatted = trimmed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
                            // eslint-disable-next-line react/no-danger -- sanitised via DOMPurify (lib/sanitize)
                            return <p key={`narr-p-${idx}`} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeHTML(formatted) }} />;
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8" data-testid="comparative-empty-state">
                          <Sparkles className="h-10 w-10 text-indigo-300 mx-auto mb-3" />
                          <p className="text-sm text-slate-600">
                            Click <strong>Generate Comparative Insights</strong> for an LLM-authored
                            head-to-head analysis you can drop straight into a board pack.
                          </p>
                        </div>
                      )}

                      {/* Auto-suppression alerts */}
                      {digestSuppressions.length > 0 && (
                        <div
                          className="rounded-lg border border-rose-200 bg-rose-50/70 p-4"
                          data-testid="comparative-suppressions-banner"
                        >
                          <div className="flex items-start gap-3">
                            <ShieldAlert className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-rose-900">
                                  {digestSuppressions.length} recipient{digestSuppressions.length === 1 ? '' : 's'} auto-suppressed
                                </span>
                                <Badge className="bg-rose-100 text-rose-700 text-xs">
                                  removed from your digests
                                </Badge>
                              </div>
                              <p className="text-xs text-rose-800 mt-1">
                                We stopped sending to bouncing or complaining addresses to protect your sender reputation.
                              </p>
                              <ul className="mt-2 space-y-1.5">
                                {digestSuppressions.slice(0, 5).map((sup) => (
                                  <li
                                    key={sup.id}
                                    className="flex items-center justify-between gap-3 text-xs bg-white border border-slate-200 rounded-md px-3 py-2 border border-rose-100"
                                    data-testid={`suppression-row-${sup.id}`}
                                  >
                                    <div className="min-w-0 flex-1">
                                      <span className="font-mono text-rose-900 truncate block">
                                        {sup.email}
                                      </span>
                                      <span className="text-rose-700/80">
                                        {sup.reason}
                                        {sup.affected_schedules?.length > 0 && (
                                          <>
                                            {' · '}
                                            {sup.affected_schedules.length} schedule
                                            {sup.affected_schedules.length === 1 ? '' : 's'} updated
                                          </>
                                        )}
                                      </span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => dismissSuppression(sup)}
                                      data-testid={`suppression-dismiss-${sup.id}`}
                                      className="h-7 w-7 p-0 hover:bg-rose-100"
                                      title="Dismiss"
                                    >
                                      <X className="h-3.5 w-3.5 text-rose-700" />
                                    </Button>
                                  </li>
                                ))}
                                {digestSuppressions.length > 5 && (
                                  <li className="text-xs text-rose-700 px-3">
                                    +{digestSuppressions.length - 5} more
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Monthly digest schedules list */}
                      {digestSchedules.length > 0 && (
                        <div
                          className="mt-2 pt-4 border-t border-slate-200"
                          data-testid="comparative-digest-schedules"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <CalendarClock className="h-4 w-4 text-indigo-600" />
                            <span className="text-sm font-semibold text-slate-700">
                              Monthly Digest Schedules
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {digestSchedules.length}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {digestSchedules.map((s) => {
                              const lastBadge = lastStatusBadge(s.last_status);
                              return (
                              <div
                                key={s.id}
                                className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                                  s.enabled
                                    ? 'bg-white border-slate-200'
                                    : 'bg-slate-50 border-slate-200 opacity-70'
                                }`}
                                data-testid={`digest-schedule-${s.id}`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-slate-800 truncate">
                                      {s.name}
                                    </span>
                                    <Badge
                                      className={
                                        s.enabled
                                          ? 'bg-emerald-100 text-emerald-700'
                                          : 'bg-slate-200 text-slate-600'
                                      }
                                    >
                                      {s.enabled ? 'Active' : 'Paused'}
                                    </Badge>
                                    <Badge
                                      className={lastBadge.className}
                                      data-testid={`digest-last-status-${s.id}`}
                                      title={
                                        s.last_status?.error
                                          ? `Last attempt: ${s.last_status.error}`
                                          : `Last attempt: ${formatScheduleDate(s.last_status?.attempted_at)}`
                                      }
                                    >
                                      {lastBadge.label}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500 flex-wrap">
                                    <span className="flex items-center gap-1">
                                      <Mail className="h-3 w-3" />
                                      {s.recipient_emails?.length || 0} recipient
                                      {(s.recipient_emails?.length || 0) === 1 ? '' : 's'}
                                    </span>
                                    <span>Day {s.day_of_month} each month</span>
                                    <span>Next: {formatScheduleDate(s.next_run_at)}</span>
                                    {s.last_status?.attempted_at && (
                                      <span>
                                        Last attempt: {formatScheduleDate(s.last_status.attempted_at)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => sendDigestNow(s)}
                                    disabled={sendingDigestId === s.id}
                                    title="Send now"
                                    data-testid={`digest-send-now-${s.id}`}
                                  >
                                    {sendingDigestId === s.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Send className="h-4 w-4" />
                                    )}
                                  </Button>
                                  <Switch
                                    checked={!!s.enabled}
                                    onCheckedChange={() => toggleDigestSchedule(s)}
                                    data-testid={`digest-toggle-${s.id}`}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteDigestSchedule(s)}
                                    title="Delete"
                                    data-testid={`digest-delete-${s.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 text-rose-500" />
                                  </Button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            )}

            {/* Schedule Monthly Digest Dialog */}
            <Dialog open={digestDialogOpen} onOpenChange={setDigestDialogOpen}>
              <DialogContent data-testid="schedule-digest-dialog">
                <DialogHeader>
                  <DialogTitle>Schedule Monthly Comparative Digest</DialogTitle>
                  <DialogDescription>
                    {cohortA?.name && cohortB?.name && (
                      <>
                        Auto-regenerate the AI narrative + PDF for{' '}
                        <strong>{cohortA.name}</strong> vs <strong>{cohortB.name}</strong> and
                        email it to selected recipients on a chosen day each month.
                      </>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-3">
                  <div className="space-y-1">
                    <Label htmlFor="digest-name">Schedule Name</Label>
                    <Input
                      id="digest-name"
                      value={digestForm.name}
                      onChange={(e) =>
                        setDigestForm((f) => ({ ...f, name: e.target.value }))
                      }
                      placeholder="e.g., Sales vs Engineering — Monthly"
                      data-testid="digest-form-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="digest-recipients">Recipients</Label>
                    <textarea
                      id="digest-recipients"
                      className="w-full min-h-[80px] rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={digestForm.recipient_emails}
                      onChange={(e) =>
                        setDigestForm((f) => ({ ...f, recipient_emails: e.target.value }))
                      }
                      placeholder="chro@example.com, vp-ld@example.com"
                      data-testid="digest-form-recipients"
                    />
                    <p className="text-xs text-slate-500">
                      Separate multiple emails with commas, semicolons, or new lines.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="digest-day">Day of Month</Label>
                      <Select
                        value={String(digestForm.day_of_month)}
                        onValueChange={(v) =>
                          setDigestForm((f) => ({ ...f, day_of_month: Number(v) }))
                        }
                      >
                        <SelectTrigger id="digest-day" data-testid="digest-form-day">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                            <SelectItem key={d} value={String(d)}>
                              Day {d}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="digest-enabled">Status</Label>
                      <div className="flex items-center gap-3 pt-2">
                        <Switch
                          id="digest-enabled"
                          checked={digestForm.enabled}
                          onCheckedChange={(v) =>
                            setDigestForm((f) => ({ ...f, enabled: v }))
                          }
                          data-testid="digest-form-enabled"
                        />
                        <span className="text-sm text-slate-600">
                          {digestForm.enabled ? 'Active on save' : 'Paused on save'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-md bg-indigo-50/60 border border-indigo-100">
                    <Switch
                      id="digest-send-test"
                      checked={digestForm.send_test}
                      onCheckedChange={(v) =>
                        setDigestForm((f) => ({ ...f, send_test: v }))
                      }
                      data-testid="digest-form-send-test"
                    />
                    <Label
                      htmlFor="digest-send-test"
                      className="text-sm leading-snug cursor-pointer"
                    >
                      <span className="font-medium text-slate-800">
                        Send a verification email now
                      </span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        Catches typo'd addresses immediately. The schedule's "Last
                        Send Status" will update to <strong>Bounced</strong> if any
                        recipient fails — no need to wait until the 1st of next month.
                      </span>
                    </Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setDigestDialogOpen(false)}
                    data-testid="digest-form-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveDigestSchedule}
                    disabled={savingDigestSchedule}
                    data-testid="digest-form-save"
                  >
                    {savingDigestSchedule ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      'Save Schedule'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Save Comparison Dialog */}
            <Dialog open={saveComparisonDialog} onOpenChange={setSaveComparisonDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Save Comparison View</DialogTitle>
                  <DialogDescription>
                    Save this comparison for quick access later
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Comparison Name</Label>
                    <Input
                      value={comparisonName}
                      onChange={(e) => setComparisonName(e.target.value)}
                      placeholder="e.g., Sales vs Engineering Q1 2026"
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    Comparing: <strong>{cohortA?.name}</strong> vs <strong>{cohortB?.name}</strong>
                  </p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSaveComparisonDialog(false)}>Cancel</Button>
                  <Button onClick={saveComparison} disabled={!comparisonName.trim()}>Save Comparison</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Alert Rules Management Card */}
            {comparisonAlertRules.length > 0 && (
              <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="alert-rules-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-base">
                    <div className="flex items-center gap-2">
                      <Bell className="h-5 w-5 text-amber-600" />
                      Comparison Alert Rules
                      <Badge className="bg-amber-100 text-amber-700">{comparisonAlertRules.length}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          fetchAlertHistory();
                          setShowAlertHistoryDialog(true);
                        }}
                        className="text-amber-600 hover:text-amber-800"
                      >
                        <Calendar className="h-4 w-4 mr-1" />
                        History
                      </Button>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Automated alerts when cohort performance gaps exceed thresholds
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {comparisonAlertRules.map((rule) => (
                      <div
                        key={rule.id}
                        className={`p-3 rounded-lg border flex items-center justify-between transition-colors ${
                          rule.is_active 
                            ? 'bg-white border-amber-200 hover:border-amber-300' 
                            : 'bg-slate-50 border-slate-200 opacity-70'
                        }`}
                        data-testid={`alert-rule-${rule.id}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={`p-2 rounded-lg ${rule.is_active ? 'bg-amber-100' : 'bg-slate-100'}`}>
                            {rule.is_active ? (
                              <Bell className="h-4 w-4 text-amber-600" />
                            ) : (
                              <BellOff className="h-4 w-4 text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">{rule.name}</p>
                              {rule.snoozed_until && new Date(rule.snoozed_until) > new Date() && (
                                <Badge variant="outline" className="text-xs bg-slate-100">
                                  Snoozed
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 truncate">
                              {rule.cohort_a_name} vs {rule.cohort_b_name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {rule.enable_gap_difference_alert && (
                                <Badge variant="outline" className="text-xs">
                                  Gap &ge; {rule.gap_difference_threshold}%
                                </Badge>
                              )}
                              {rule.enable_baseline_drop_alert && (
                                <Badge variant="outline" className="text-xs">
                                  Drop &le; {rule.baseline_drop_threshold}%
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-xs capitalize">
                                {rule.frequency}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleAlertRule(rule.id)}
                            title={rule.is_active ? 'Disable alert' : 'Enable alert'}
                            className="h-8 w-8 p-0"
                          >
                            {rule.is_active ? (
                              <Bell className="h-4 w-4 text-amber-600" />
                            ) : (
                              <BellOff className="h-4 w-4 text-slate-400" />
                            )}
                          </Button>
                          {rule.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => snoozeAlertRule(rule.id, 24)}
                              title="Snooze for 24 hours"
                              className="h-8 w-8 p-0"
                            >
                              <AlertCircle className="h-4 w-4 text-slate-400" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteAlertRule(rule.id)}
                            title="Delete alert rule"
                            className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Active Comparison Alerts Banner */}
            {activeComparisonAlerts.length > 0 && (
              <Card className="border-2 border-rose-300 bg-gradient-to-r from-rose-50 to-orange-50" data-testid="active-comparison-alerts">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base text-rose-700">
                    <AlertCircle className="h-5 w-5" />
                    Active Alerts
                    <Badge className="bg-rose-100 text-rose-700">{activeComparisonAlerts.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {activeComparisonAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className="p-3 rounded-lg bg-white border border-rose-200 flex items-start justify-between gap-3"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm text-rose-800">{alert.message}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {alert.rule_name} • {new Date(alert.triggered_at).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => dismissAlert(alert.id)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          Dismiss
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Create Alert Rule Dialog */}
            <Dialog open={showAlertRuleDialog} onOpenChange={setShowAlertRuleDialog}>
              <DialogContent className="sm:max-w-lg" data-testid="create-alert-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-amber-500" />
                    Create Comparison Alert
                  </DialogTitle>
                  <DialogDescription>
                    Get notified when performance gaps between cohorts exceed your thresholds
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  {/* Alert Name */}
                  <div className="space-y-2">
                    <Label htmlFor="alert-name">Alert Name</Label>
                    <Input
                      id="alert-name"
                      value={newAlertRule.name}
                      onChange={(e) => setNewAlertRule(r => ({ ...r, name: e.target.value }))}
                      placeholder="e.g., Sales vs Engineering Performance Alert"
                      data-testid="alert-name-input"
                    />
                  </div>
                  
                  {/* Cohorts info */}
                  <div className="p-3 rounded-lg bg-slate-50 border">
                    <p className="text-sm text-slate-600">
                      Monitoring: <strong className="text-indigo-700">{cohortA?.name}</strong>
                      {' vs '}
                      <strong className="text-purple-700">{cohortB?.name}</strong>
                    </p>
                  </div>
                  
                  {/* Recommended Thresholds */}
                  {recommendedThresholds && (
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="h-4 w-4 text-blue-600" />
                        <p className="text-sm font-medium text-blue-800">Recommended Settings</p>
                        <Badge className={`ml-auto text-xs ${
                          recommendedThresholds.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                          recommendedThresholds.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {recommendedThresholds.confidence} confidence
                        </Badge>
                      </div>
                      <p className="text-xs text-blue-600">
                        Based on historical variance: Gap threshold {recommendedThresholds.gap_difference_threshold}%, 
                        Baseline drop {recommendedThresholds.baseline_drop_threshold}%
                      </p>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-blue-600 p-0 h-auto mt-1"
                        onClick={() => setNewAlertRule(r => ({
                          ...r,
                          gap_difference_threshold: recommendedThresholds.gap_difference_threshold,
                          baseline_drop_threshold: recommendedThresholds.baseline_drop_threshold
                        }))}
                      >
                        Apply recommended settings
                      </Button>
                    </div>
                  )}
                  
                  {/* Trigger Conditions */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-slate-700">Trigger Conditions</h4>
                    
                    {/* Gap Difference Alert */}
                    <div className="space-y-2 p-3 rounded-lg border bg-white">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="gap-diff-alert" className="flex items-center gap-2 cursor-pointer">
                          Gap Difference Alert
                        </Label>
                        <Switch
                          id="gap-diff-alert"
                          checked={newAlertRule.enable_gap_difference_alert}
                          onCheckedChange={(checked) => setNewAlertRule(r => ({ ...r, enable_gap_difference_alert: checked }))}
                        />
                      </div>
                      {newAlertRule.enable_gap_difference_alert && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm text-slate-500">Alert when gap difference exceeds</span>
                          <Input
                            type="number"
                            value={newAlertRule.gap_difference_threshold}
                            onChange={(e) => setNewAlertRule(r => ({ ...r, gap_difference_threshold: parseFloat(e.target.value) || 0 }))}
                            className="w-20"
                            step="0.5"
                            min="1"
                            max="100"
                          />
                          <span className="text-sm text-slate-500">%</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Baseline Drop Alert */}
                    <div className="space-y-2 p-3 rounded-lg border bg-white">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="baseline-drop-alert" className="flex items-center gap-2 cursor-pointer">
                          Baseline Drop Alert
                        </Label>
                        <Switch
                          id="baseline-drop-alert"
                          checked={newAlertRule.enable_baseline_drop_alert}
                          onCheckedChange={(checked) => setNewAlertRule(r => ({ ...r, enable_baseline_drop_alert: checked }))}
                        />
                      </div>
                      {newAlertRule.enable_baseline_drop_alert && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm text-slate-500">Alert when either cohort drops below</span>
                          <Input
                            type="number"
                            value={newAlertRule.baseline_drop_threshold}
                            onChange={(e) => setNewAlertRule(r => ({ ...r, baseline_drop_threshold: parseFloat(e.target.value) || 0 }))}
                            className="w-20"
                            step="0.5"
                            max="0"
                          />
                          <span className="text-sm text-slate-500">%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Notification Settings */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-slate-700">Notification Settings</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-3 rounded-lg border bg-white">
                        <Label htmlFor="in-app-notif" className="cursor-pointer text-sm">In-App</Label>
                        <Switch
                          id="in-app-notif"
                          checked={newAlertRule.in_app_notification}
                          onCheckedChange={(checked) => setNewAlertRule(r => ({ ...r, in_app_notification: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg border bg-white">
                        <Label htmlFor="email-notif" className="cursor-pointer text-sm">Email</Label>
                        <Switch
                          id="email-notif"
                          checked={newAlertRule.email_notification}
                          onCheckedChange={(checked) => setNewAlertRule(r => ({ ...r, email_notification: checked }))}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Frequency */}
                  <div className="space-y-2">
                    <Label>Check Frequency</Label>
                    <Select
                      value={newAlertRule.frequency}
                      onValueChange={(value) => setNewAlertRule(r => ({ ...r, frequency: value }))}
                    >
                      <SelectTrigger data-testid="frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="realtime">Real-time (on data import)</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAlertRuleDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={createAlertRule}
                    disabled={!newAlertRule.name.trim() || (!newAlertRule.enable_gap_difference_alert && !newAlertRule.enable_baseline_drop_alert)}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="create-alert-submit"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Create Alert
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Alert History Dialog */}
            <Dialog open={showAlertHistoryDialog} onOpenChange={setShowAlertHistoryDialog}>
              <DialogContent className="sm:max-w-lg" data-testid="alert-history-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-slate-500" />
                    Alert History
                  </DialogTitle>
                  <DialogDescription>
                    View past alerts triggered by your comparison rules
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4 max-h-96 overflow-y-auto">
                  {alertHistory.length > 0 ? (
                    <div className="space-y-2">
                      {alertHistory.map((alert) => (
                        <div
                          key={alert.id}
                          className={`p-3 rounded-lg border ${
                            alert.dismissed 
                              ? 'bg-slate-50 border-slate-200' 
                              : 'bg-amber-50 border-amber-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${alert.dismissed ? 'text-slate-600' : 'text-amber-800'}`}>
                                {alert.message}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                {alert.rule_name} • {new Date(alert.triggered_at).toLocaleString()}
                              </p>
                            </div>
                            {alert.dismissed && (
                              <Badge variant="outline" className="text-xs">Dismissed</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No alert history yet</p>
                      <p className="text-xs text-slate-400 mt-1">Alerts will appear here when triggered</p>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAlertHistoryDialog(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Standard Gap Analysis UI (when not in comparison mode) */}
        {!comparisonMode && (
          <>
        {/* Active Alerts Section */}
        {alertData?.alerts_enabled && alertData?.alerts?.length > 0 && (
          <Collapsible open={alertsExpanded} onOpenChange={setAlertsExpanded}>
            <Card className={`border-2 ${
              alertData.summary.critical > 0 ? 'border-rose-300 bg-rose-50/50' :
              alertData.summary.warning > 0 ? 'border-amber-300 bg-amber-50/50' :
              'border-emerald-300 bg-emerald-50/50'
            }`}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-white/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      {alertData.summary.critical > 0 ? (
                        <AlertCircle className="h-5 w-5 text-rose-500" />
                      ) : alertData.summary.warning > 0 ? (
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                      ) : (
                        <Sparkles className="h-5 w-5 text-emerald-500" />
                      )}
                      Active Alerts
                      <div className="flex gap-2 ml-2">
                        {alertData.summary.critical > 0 && (
                          <Badge className="bg-rose-100 text-rose-700 border-rose-200">
                            {alertData.summary.critical} Critical
                          </Badge>
                        )}
                        {alertData.summary.warning > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                            {alertData.summary.warning} Warning
                          </Badge>
                        )}
                        {alertData.summary.exceeding > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            {alertData.summary.exceeding} Exceeding
                          </Badge>
                        )}
                      </div>
                    </CardTitle>
                    <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${alertsExpanded ? 'rotate-90' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {alertData.alerts.map((alert, index) => (
                      <div
                        key={alert.id || alert.message?.slice(0, 20) || `alert-${index}`}
                        className={`p-3 rounded-lg border flex items-start gap-3 ${
                          alert.level === 'critical' ? 'bg-rose-100 border-rose-200' :
                          alert.level === 'warning' ? 'bg-amber-100 border-amber-200' :
                          'bg-emerald-100 border-emerald-200'
                        }`}
                      >
                        {alert.level === 'critical' ? (
                          <AlertCircle className="h-5 w-5 text-rose-600 mt-0.5 flex-shrink-0" />
                        ) : alert.level === 'warning' ? (
                          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <Award className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm ${
                            alert.level === 'critical' ? 'text-rose-800' :
                            alert.level === 'warning' ? 'text-amber-800' :
                            'text-emerald-800'
                          }`}>
                            {alert.message}
                          </p>
                          <p className="text-xs text-slate-600 mt-1">
                            Projected: {alert.projected}% | Actual: {alert.actual}% | Gap: {alert.gap >= 0 ? '+' : ''}{alert.gap}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Thresholds: Warning at {alertData.thresholds?.warning}% | Critical at {alertData.thresholds?.critical}% | Exceeding at +{alertData.thresholds?.improvement}%
                  </p>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Alerts Disabled Notice */}
        {alertData && !alertData.alerts_enabled && (
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <BellOff className="h-5 w-5 text-slate-400" />
                <p className="text-sm text-slate-600">
                  Alerts are currently disabled.{' '}
                  <button 
                    onClick={() => setShowAlertSettings(true)} 
                    className="text-purple-600 hover:underline font-medium"
                  >
                    Enable alerts
                  </button>
                  {' '}to receive notifications when KPIs deviate from projections.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Executive Summary — AI narrative of where value conversion broke down (R10) */}
        {hasData && (
          <Collapsible open={narrativeExpanded} onOpenChange={setNarrativeExpanded}>
            <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white" data-testid="executive-summary-card">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-indigo-50/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Brain className="h-5 w-5 text-indigo-600" />
                      Executive Summary
                      {aiNarrative?.generated_at && (
                        <Badge variant="outline" className="ml-2 text-xs font-normal">
                          Generated {formatNarrativeDate(aiNarrative.generated_at)}
                        </Badge>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          generateNarrative();
                        }}
                        disabled={generatingNarrative}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        {generatingNarrative ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Zap className="h-4 w-4 mr-2" />
                            {aiNarrative?.narrative ? 'Regenerate' : 'Generate Insights'}
                          </>
                        )}
                      </Button>
                      <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${narrativeExpanded ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                  <CardDescription>
                    AI-generated executive narrative of where value conversion broke down — and what to do about it
                  </CardDescription>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  {aiNarrative?.narrative ? (
                    <div className="space-y-4">
                      <div className="prose prose-slate prose-sm max-w-none">
                        {aiNarrative.narrative.split('\n').map((paragraph, idx) => {
                          if (!paragraph.trim()) return null;
                          
                          // Handle headers
                          if (paragraph.startsWith('##')) {
                            return (
                              <h3 key={`gap-h-${idx}`} className="text-lg font-semibold text-slate-800 mt-4 mb-2 flex items-center gap-2">
                                {paragraph.includes('Overview') && <BarChart3 className="h-5 w-5 text-indigo-500" />}
                                {paragraph.includes('Broke') && <Lightbulb className="h-5 w-5 text-amber-500" />}
                                {paragraph.includes('Recommend') && <ThumbsUp className="h-5 w-5 text-emerald-500" />}
                                {paragraph.includes('Confidence') && <Target className="h-5 w-5 text-blue-500" />}
                                {paragraph.replace(/^#+\s*\**|\**$/g, '')}
                              </h3>
                            );
                          }
                          
                          // Handle bullet points
                          if (paragraph.trim().startsWith('-') || paragraph.trim().startsWith('•') || paragraph.trim().match(/^\d+\./)) {
                            return (
                              <div key={`gap-b-${idx}`} className="flex items-start gap-2 ml-4 text-slate-600">
                                <span className="text-indigo-400 mt-1">•</span>
                                <span>{paragraph.replace(/^[-•]\s*|\d+\.\s*/, '')}</span>
                              </div>
                            );
                          }
                          
                          // Handle bold text markers
                          if (paragraph.includes('**')) {
                            const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
                            return (
                              <p key={`gap-p-${idx}`} className="text-slate-600 leading-relaxed">
                                {parts.map((part, partIdx) => {
                                  if (part.startsWith('**') && part.endsWith('**')) {
                                    return <strong key={`bold-${partIdx}`} className="text-slate-800">{part.slice(2, -2)}</strong>;
                                  }
                                  return part;
                                })}
                              </p>
                            );
                          }
                          
                          return (
                            <p key={`gap-t-${idx}`} className="text-slate-600 leading-relaxed">
                              {paragraph}
                            </p>
                          );
                        })}
                      </div>
                      
                      {/* Summary Stats from AI */}
                      {aiNarrative.summary && (
                        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-sm">
                              <Award className="h-4 w-4 text-emerald-500" />
                              <span className="text-slate-600">{aiNarrative.summary.exceeding} ahead of course</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-blue-500" />
                              <span className="text-slate-600">{aiNarrative.summary.on_track} on course</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                              <span className="text-slate-600">{aiNarrative.summary.below_target} off course</span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={exportNarrativePdf}
                            className="flex items-center gap-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                          >
                            <Download className="h-4 w-4" />
                            Export PDF
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MessageSquareText className="h-12 w-12 text-indigo-300 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-slate-700 mb-2">No Executive Summary Yet</h4>
                      <p className="text-slate-500 text-sm max-w-md mx-auto mb-4">
                        Click "Generate Insights" for an AI executive narrative of where value conversion broke down — which KPIs are off-course, why, and what to do about it.
                      </p>
                      <Button
                        onClick={generateNarrative}
                        disabled={generatingNarrative}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        {generatingNarrative ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Brain className="h-4 w-4 mr-2" />
                            Generate AI Insights
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Missing Middle Loss Waterfall + Navigation Friction (R6 + R7) */}
        {hasData && (
          vcContext?.potential_value ? (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
              <div className="xl:col-span-2">
                <LossWaterfall
                  gapRows={Object.values(gapData?.gap_analysis || {})}
                  stages={vcContext?.journey_stages}
                  potential={vcContext?.potential_value}
                />
              </div>
              <FrictionPanel orgId={frictionOrgId} cohortId="all" getHeaders={frictionHeaders} />
            </div>
          ) : (
            <FrictionPanel orgId={frictionOrgId} cohortId="all" getHeaders={frictionHeaders} />
          )
        )}

        {/* Industry Benchmark Overview */}
        {benchmarkOverview && (
          <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50" data-testid="benchmark-overview-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-base">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-600" />
                  Missing Middle Benchmark
                  <Badge className="bg-indigo-100 text-indigo-700 ml-2">
                    {benchmarkOverview.industry_name}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.location.href = '/benchmarking'}
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  View Details
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                  <p className="text-xs text-slate-500 mb-1">Industry Value Conversion Efficiency</p>
                  <p className="text-xl font-bold text-indigo-700">
                    {(100 + (benchmarkOverview.industry_median_gap || 0)).toFixed(0)}%
                  </p>
                </div>
                {benchmarkOverview.has_data ? (
                  <>
                    <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                      <p className="text-xs text-slate-500 mb-1">Your Conversion Efficiency</p>
                      <p className="text-xl font-bold text-slate-800">
                        {(100 + (benchmarkOverview.your_average_gap || 0)).toFixed(0)}%
                      </p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                      <p className="text-xs text-slate-500 mb-1">Your Percentile</p>
                      <p className="text-xl font-bold text-purple-700">
                        {benchmarkOverview.percentile_rank?.toFixed(0)}
                        <span className="text-sm font-normal text-slate-500">th</span>
                      </p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                      <p className="text-xs text-slate-500 mb-1">Performance</p>
                      <Badge className={`${
                        benchmarkOverview.performance_label === 'Top Performer' ? 'bg-emerald-100 text-emerald-700' :
                        benchmarkOverview.performance_label === 'Above Average' ? 'bg-emerald-100 text-emerald-700' :
                        benchmarkOverview.performance_label === 'Average' ? 'bg-blue-100 text-blue-700' :
                        benchmarkOverview.performance_label === 'Below Average' ? 'bg-amber-100 text-amber-700' :
                        'bg-rose-100 text-rose-700'
                      }`}>
                        {benchmarkOverview.performance_label}
                      </Badge>
                    </div>
                  </>
                ) : (
                  <div className="col-span-3 flex items-center justify-center p-3 bg-white rounded-lg border border-indigo-100">
                    <p className="text-sm text-slate-500">
                      Import KPI data to see your position vs {benchmarkOverview.sample_size}+ organizations
                    </p>
                  </div>
                )}
              </div>
              <p className="mt-2 text-[11px] text-slate-500">Value Conversion Efficiency = share of expected value realized (100% + average gap) · modeled</p>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Calculator className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{summary.kpis_with_projections || 0}</p>
                  <p className="text-sm text-slate-600">KPIs with Projections</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <Database className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{summary.kpis_with_actuals || 0}</p>
                  <p className="text-sm text-slate-600">KPIs with Actuals</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Target className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{summary.kpis_with_both || 0}</p>
                  <p className="text-sm text-slate-600">KPIs Comparable</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-2 ${summary.average_gap >= 0 ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-white' : 'border-amber-300 bg-gradient-to-br from-amber-50 to-white'}`}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${summary.average_gap >= 0 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                  {summary.average_gap >= 0 ? (
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-amber-600" />
                  )}
                </div>
                <div>
                  <p className={`text-2xl font-bold ${summary.average_gap >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {summary.average_gap >= 0 ? '+' : ''}{summary.average_gap?.toFixed(1) || 0}%
                  </p>
                  <p className="text-sm text-slate-600">Value Conversion Gap</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation Status Panel (R5) */}
        {hasData && (
          <Card data-testid="navigation-status-panel">
            <CardHeader>
              <CardTitle className="text-lg">Navigation Status</CardTitle>
              <CardDescription>Are we navigating successfully — or leaking value en route?</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <Award className="h-6 w-6 text-emerald-600" />
                  <div>
                    <p className="text-2xl font-bold text-emerald-700">{summary.exceeding_projection || 0}</p>
                    <p className="text-sm text-emerald-600">Navigating Successfully</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <CheckCircle2 className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold text-blue-700">{summary.on_track || 0}</p>
                    <p className="text-sm text-blue-600">Stable</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                  <div>
                    <p className="text-2xl font-bold text-amber-700">{summary.below_target || 0}</p>
                    <p className="text-sm text-amber-600">Requires Intervention</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <Database className="h-6 w-6 text-slate-500" />
                  <div>
                    <p className="text-2xl font-bold text-slate-700">
                      {(summary.kpis_with_projections || 0) - (summary.kpis_with_both || 0)}
                    </p>
                    <p className="text-sm text-slate-500">Insufficient Visibility</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comparison Chart */}
        {chartData.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-600" />
                Value Conversion Outcomes
              </CardTitle>
              <CardDescription>
                Compare expected value creation against realised value creation to identify where navigation performance diverged.
                {' '}Each row is one KPI. <span className="font-semibold text-purple-600">Purple</span> = the
                improvement % we <em>planned</em> for in the latest scenario.
                {' '}<span className="font-semibold text-emerald-600">Green</span> = the
                improvement % we <em>actually achieved</em>. Both bars use a
                direction-adjusted convention: <strong>positive % = better</strong>,
                regardless of whether the KPI is "higher is better" (e.g. CSAT)
                or "lower is better" (e.g. Error Rate, Cost per Unit).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ height: Math.max(320, chartData.length * 56) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ left: 40, right: 60, top: 4, bottom: 4 }}
                    barCategoryGap={14}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`}
                      stroke="#64748b"
                      fontSize={12}
                      domain={[
                        (dataMin) => Math.min(0, Math.floor(dataMin - 1)),
                        (dataMax) => Math.ceil(dataMax + 2),
                      ]}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={210}
                      stroke="#475569"
                      fontSize={12}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="top"
                      height={36}
                      iconType="circle"
                      formatter={(value) => {
                        if (value === 'Projected') {
                          return <span className="text-slate-700"><strong>Projected</strong> (latest scenario)</span>;
                        }
                        return <span className="text-slate-700"><strong>Actual</strong> (Q1 2026)</span>;
                      }}
                    />
                    <ReferenceLine x={0} stroke="#94a3b8" strokeWidth={1.5} />
                    <Bar
                      dataKey="projected"
                      name="Projected"
                      fill="#a855f7"
                      radius={[0, 4, 4, 0]}
                      barSize={16}
                      label={{
                        position: 'right',
                        formatter: (v) => v != null ? `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%` : '',
                        fill: '#7c3aed', fontSize: 10, fontWeight: 600,
                      }}
                    />
                    <Bar
                      dataKey="actual"
                      name="Actual"
                      fill="#10b981"
                      radius={[0, 4, 4, 0]}
                      barSize={16}
                      label={{
                        position: 'right',
                        formatter: (v) => v != null ? `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%` : '',
                        fill: '#047857', fontSize: 10, fontWeight: 600,
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Per-row status pills under the chart so the user can
                  see at a glance which KPIs beat / missed projection.
                  Pulls colours from the shared STATUS_CONFIG so the
                  pills, the summary tiles in the header, and the
                  Detailed KPI Breakdown below all speak the same
                  visual language. */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                {chartData.map((d) => {
                  const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.no_actual_data;
                  const StatusIcon = cfg.icon;
                  return (
                    <div key={d.kpiId || d.name} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border border-slate-200 bg-white">
                      <span className="font-medium text-slate-700 truncate" title={d.fullName}>{d.name}</span>
                      <span className={cn(
                        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold border',
                        cfg.bgColor, cfg.color, cfg.borderColor,
                      )}>
                        <StatusIcon className="h-3 w-3" />
                        {cfg.label}
                        {typeof d.gap === 'number' && (
                          <span className="font-mono">
                            ({d.gap > 0 ? '+' : ''}{d.gap.toFixed(1)}%)
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : !hasProjections ? (
          <Card className="border-purple-200">
            <CardContent className="py-12 text-center">
              <Calculator className="h-12 w-12 text-purple-700 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Projections Yet</h3>
              <p className="text-slate-500 mb-4 max-w-md mx-auto">
                Run ROI calculations to generate projected KPI improvements, then import actual data to compare.
              </p>
              <Button onClick={() => navigate('/configure')} className="bg-purple-600 hover:bg-purple-700">
                Run ROI Calculator
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : !hasActuals ? (
          <Card className="border-rose-200">
            <CardContent className="py-12 text-center">
              <Database className="h-12 w-12 text-rose-700 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Actual KPI Data</h3>
              <p className="text-slate-500 mb-4 max-w-md mx-auto">
                Import actual KPI data to compare against your projections and see real-world gap analysis.
              </p>
              <Button onClick={() => navigate('/data-management')} className="bg-rose-600 hover:bg-rose-700">
                Import KPI Data
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {/* Trend Analysis Section */}
        {trendChartData.length > 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LineChartIcon className="h-5 w-5 text-indigo-600" />
                Gap Trend Analysis
              </CardTitle>
              <CardDescription>
                Track how the gap between projections and actuals evolves over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="overview" className="space-y-4">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                  <TabsTrigger value="overview">Overall Gap Trend</TabsTrigger>
                  <TabsTrigger value="kpi">Per-KPI Trends</TabsTrigger>
                </TabsList>
                
                <TabsContent value="overview" className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="h-4 w-4" />
                      <span>{trendChartData.length} data points tracked</span>
                    </div>
                    {trendChartData.length > 0 && (
                      <Badge variant="outline" className={
                        trendChartData[trendChartData.length - 1]?.averageGap >= 0 
                          ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                          : 'border-rose-300 text-rose-700 bg-rose-50'
                      }>
                        Latest Gap: {trendChartData[trendChartData.length - 1]?.averageGap >= 0 ? '+' : ''}
                        {trendChartData[trendChartData.length - 1]?.averageGap?.toFixed(2)}%
                      </Badge>
                    )}
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trendChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gapGradientPositive" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="gapGradientNegative" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#64748b"
                          fontSize={12}
                        />
                        <YAxis 
                          tickFormatter={(v) => `${v}%`}
                          stroke="#64748b"
                          fontSize={12}
                        />
                        <Tooltip content={<TrendTooltip />} />
                        <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                        <Area
                          type="monotone"
                          dataKey="averageGap"
                          name="Average Gap"
                          stroke="#6366f1"
                          strokeWidth={2}
                          fill="url(#gapGradientPositive)"
                          dot={{ fill: '#6366f1', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, stroke: '#6366f1', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-sm text-slate-500 text-center">
                    Gap above zero = exceeding projections | Gap below zero = underperforming
                  </p>
                </TabsContent>
                
                <TabsContent value="kpi" className="space-y-4">
                  {availableKpis.length > 0 && (
                    <>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-slate-600">Select KPI:</span>
                        <Select value={selectedKpi} onValueChange={setSelectedKpi}>
                          <SelectTrigger className="w-64">
                            <SelectValue placeholder="Choose a KPI to view trends" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableKpis.map(kpi => (
                              <SelectItem key={kpi.id} value={kpi.id}>
                                {kpi.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {selectedKpi && (
                        <div className="h-72">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={getKpiTrendData(selectedKpi)} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis 
                                dataKey="date" 
                                stroke="#64748b"
                                fontSize={12}
                              />
                              <YAxis 
                                tickFormatter={(v) => `${v}%`}
                                stroke="#64748b"
                                fontSize={12}
                              />
                              <Tooltip content={<TrendTooltip />} />
                              <Legend />
                              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                              <Line 
                                type="monotone" 
                                dataKey="projected" 
                                name="Projected Target" 
                                stroke="#a855f7" 
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="actual" 
                                name="Actual Improvement" 
                                stroke="#10b981" 
                                strokeWidth={2}
                                dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                                activeDot={{ r: 6 }}
                              />
                              <Area
                                type="monotone"
                                dataKey="gap"
                                name="Gap vs Target"
                                fill="#6366f180"
                                stroke="#6366f1"
                                strokeWidth={1}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                      
                      {!selectedKpi && (
                        <div className="flex items-center justify-center h-72 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                          <div className="text-center">
                            <LineChartIcon className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500">Select a KPI to view its trend over time</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Trend Analysis Empty State */}
        {trendChartData.length <= 1 && hasActuals && (
          <Card className="border-indigo-200 bg-indigo-50/30">
            <CardContent className="py-8 text-center">
              <LineChartIcon className="h-10 w-10 text-indigo-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">Trend Analysis Coming Soon</h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                Import KPI data for at least 2 different time periods to see how the gap between projections and actuals evolves over time.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Detailed KPI Breakdown */}
        {gapData?.gap_analysis && Object.keys(gapData.gap_analysis).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Value Conversion Breakdown</CardTitle>
              <CardDescription>Per-KPI conversion of expected value into realized value — with Missing Middle attribution for off-course KPIs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.values(gapData.gap_analysis)
                  .sort((a, b) => {
                    // Sort by status priority, then by gap magnitude
                    const statusOrder = { significantly_below: 0, below_target: 1, on_track: 2, exceeding: 3, no_actual_data: 4, no_projection: 5 };
                    if (statusOrder[a.status] !== statusOrder[b.status]) {
                      return statusOrder[a.status] - statusOrder[b.status];
                    }
                    return Math.abs(b.gap) - Math.abs(a.gap);
                  })
                  .map(kpi => {
                    const statusConfig = STATUS_CONFIG[kpi.status] || STATUS_CONFIG.no_actual_data;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <div 
                        key={kpi.kpi_id}
                        className={`p-4 rounded-lg border-2 ${statusConfig.borderColor} ${statusConfig.bgColor} transition-all hover:shadow-sm`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`p-2 rounded-lg bg-white border border-slate-200`}>
                              <StatusIcon className={`h-5 w-5 ${statusConfig.color}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium text-slate-800">{kpi.kpi_name}</h4>
                                <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border ${statusConfig.borderColor}`}>
                                  {statusConfig.label}
                                </Badge>
                              </div>
                              
                              {kpi.has_projection && kpi.has_actual ? (
                                <div className="mt-3 space-y-2">
                                  {/* Progress comparison */}
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <p className="text-slate-500 mb-1">Projected</p>
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-white rounded-full h-2">
                                          <div 
                                            className="bg-purple-500 h-2 rounded-full"
                                            style={{ width: `${Math.min(Math.max(kpi.projected_improvement, 0), 100)}%` }}
                                          />
                                        </div>
                                        <span className="font-semibold text-purple-600 w-14 text-right">
                                          {kpi.projected_improvement?.toFixed(1)}%
                                        </span>
                                      </div>
                                    </div>
                                    <div>
                                      <p className="text-slate-500 mb-1">Actual</p>
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-white rounded-full h-2">
                                          <div 
                                            className="bg-emerald-500 h-2 rounded-full"
                                            style={{ width: `${Math.min(Math.max(kpi.actual_improvement, 0), 100)}%` }}
                                          />
                                        </div>
                                        <span className="font-semibold text-emerald-600 w-14 text-right">
                                          {kpi.actual_improvement?.toFixed(1)}%
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Gap indicator */}
                                  <div className="flex items-center justify-between pt-2 border-t border-white/50">
                                    <span className="text-sm text-slate-500">
                                      {kpi.actual_data_points} data point{kpi.actual_data_points !== 1 ? 's' : ''} 
                                      ({formatDate(kpi.first_actual_date)} → {formatDate(kpi.latest_actual_date)})
                                    </span>
                                    <span className={cn(
                                      'font-bold',
                                      kpi.gap >= 0 ? 'text-emerald-700' : 'text-rose-700',
                                    )}>
                                      Gap: {kpi.gap >= 0 ? '+' : ''}{kpi.gap?.toFixed(1)}%
                                    </span>
                                  </div>

                                  {/* Missing Middle attribution + modeled projection confidence */}
                                  <KpiConversionInsight kpi={kpi} stages={vcContext?.journey_stages} />
                                </div>
                              ) : kpi.has_projection && !kpi.has_actual ? (
                                <div className="mt-2 flex items-center justify-between">
                                  <p className="text-sm text-slate-500">
                                    Projected: <span className="font-semibold text-purple-600">{kpi.projected_improvement?.toFixed(1)}%</span>
                                  </p>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => navigate('/data-management')}
                                    className="text-xs"
                                  >
                                    Import Actual Data
                                  </Button>
                                </div>
                              ) : !kpi.has_projection && kpi.has_actual ? (
                                <div className="mt-2 flex items-center justify-between">
                                  <p className="text-sm text-slate-500">
                                    Actual: <span className="font-semibold text-emerald-600">{kpi.actual_improvement?.toFixed(1)}%</span>
                                  </p>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => navigate('/configure')}
                                    className="text-xs"
                                  >
                                    Run Projection
                                  </Button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* How Gap Analysis Works */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Info className="h-5 w-5 text-slate-500" />
              How Value Conversion Analysis Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-xs">1</div>
                  <span className="font-medium text-purple-800">Run ROI Projections</span>
                </div>
                <p className="text-slate-600">
                  Use the Impact Calculators to model expected KPI improvements from your learning investments.
                </p>
              </div>
              <div className="p-4 bg-rose-50 rounded-lg border border-rose-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-rose-200 flex items-center justify-center text-rose-700 font-bold text-xs">2</div>
                  <span className="font-medium text-rose-800">Import Actual KPIs</span>
                </div>
                <p className="text-slate-600">
                  Periodically import real KPI data via Data Management to track actual business performance.
                </p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 font-bold text-xs">3</div>
                  <span className="font-medium text-emerald-800">Detect Value Leakage</span>
                </div>
                <p className="text-slate-600">
                  Compare expected vs realized value creation to find where capability failed to convert into confidence, coordination, and outcomes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
          </>
        )}
    </div>
    </PageShell>
  );
}
